import React, { useEffect, useRef, useState } from 'react';
import Layout from '../../components/Layout/Layout';
import { callApi } from '../../api/callApi';
import { API_ENDPOINT, API_URL_2 } from '../../api/constant';
import { useForm, useWatch } from 'react-hook-form';
import { showToastNotification } from '../../utility/helper';
import InputField from '../../components/UI/Forms/InputField';
import ErrorMessage from '../../components/UI/Notifications/ErrorMessage';
import Button from '../../components/UI/Buttons/Button';
import useImageUpload from '../../hooks/useImageUpload';
import { ICategory } from '../../interfaces/category';
import Checkbox from '../../components/UI/Forms/Checkbox';
import useFileDelete from '../../hooks/useFileDelete';
import { useNavigate, useParams } from 'react-router-dom';
import SeoForm, { SeoFormRef, submitSEOData } from '../../components/common/SEOForm';
import { SeoFormData } from '../../interfaces/seo';

const AddCategory: React.FC = () => {
    const { uploadImage, error } = useImageUpload();
    const { deleteFile } = useFileDelete();
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();

    const [loading, setLoading] = useState<boolean>(true);
    const { register, handleSubmit, formState: { errors }, setValue, control } = useForm({ mode: 'onBlur' });
    const [imageURL, setImageURL] = useState<string>('');
    const [loadingButton, setLoadingButton] = useState<boolean>(false);
    const [editingCategory, setEditingCategory] = useState<ICategory | null>(null);
    const [parentCategory, setParentCategory] = useState<ICategory | null>(null);
    const [isSubCategory, setIsSubCategory] = useState<boolean>(false);
    const [isSlugTouched, setIsSlugTouched] = useState(false);
    const urlParams = new URLSearchParams(window.location.search);
    const parentId = urlParams.get('parentId');
    const type = urlParams.get('type');
    const seoFormRef = useRef<SeoFormRef>(null);
    const [seoData, setSeoData] = useState<SeoFormData>({
        entityType: 'category',
    });
    const titleValue = useWatch({
        control,
        name: 'title',
    });
    useEffect(() => {
        if (titleValue && !isSlugTouched) {
            const generatedSlug = generateSlug(titleValue);
            setValue('slug', generatedSlug);
        }
    }, [titleValue, setValue, control, isSlugTouched]);

    const generateSlug = (text: string) => {
        return text
            .toLowerCase()
            .replace(/\s+/g, '-')
            .replace(/[^a-z0-9-]/g, '')
            .replace(/-+/g, '-')
            .replace(/^-|-$/g, '');
    };


    const handleSlugFocus = () => {
        setIsSlugTouched(true);
    };

    useEffect(() => {
        if (id) {
            // Editing existing category
            getCategoryById(id);
        } else if (parentId && type === 'sub') {
            // Adding subcategory
            setIsSubCategory(true);
            getCategoryById(parentId);
        }

        if (!id && !parentId) {
            setLoading(false);
        }
    }, [id, parentId, type]);

    const getCategoryById = async (categoryId: string) => {
        try {
            setLoading(true);
            const response = await callApi(API_URL_2 + API_ENDPOINT.page + `categories/${categoryId}`, { method: 'GET' });

            if (id) {
                // Editing mode
                setEditingCategory(response);
                setFormValues(response);
            } else if (parentId) {
                // Adding subcategory mode
                setParentCategory(response);
            }

        } catch (error: any) {
            console.error('Failed to fetch category:', error.message);
            showToastNotification('danger', 'Failed to load category');
        } finally {
            setLoading(false);
        }
    };

    const setFormValues = (category: ICategory) => {
        setValue('title', category.title);
        setValue('isRecommended', category.isRecommended);
        setValue('brand', category.brand);
        setValue('valueAddition', category.valueAddition);
        setValue('origin', category.origin);
        setValue('state', category.state);
        setValue('grade', category.grade);
        setValue('cut', category.cut);
        setValue('slug', category.slug);
        if (category.seo) {
            setSeoData(category.seo);
        }
        if (category.slug) {
            setIsSlugTouched(true);
        }
        setImageURL(category.image);

        // Check if it's a subcategory
        if (category.type === 'sub') {
            setIsSubCategory(true);
        }
    };

    const onSubmit = async (data: any) => {
        setLoadingButton(true);

        const postData = {
            image: imageURL,
            title: data.title,
            slug: data.slug,
            isRecommended: data.isRecommended ?? false,
            categoryId: isSubCategory ? (editingCategory?.categoryId || parentId) : null,
            type: isSubCategory ? 'sub' : 'primary',
            state: data?.state ?? false,
            brand: data?.brand ?? false,
            origin: data?.origin ?? false,
            valueAddition: data?.valueAddition ?? false,
            grade: data?.grade ?? false,
            cut: data?.cut ?? false,
        };

        try {
            let categoryId = editingCategory ? editingCategory.id : '';
            if (editingCategory) {
                // Update category
                await callApi(API_URL_2 + API_ENDPOINT.page + `categories/${editingCategory.id}`, {
                    method: 'PUT',
                    data: postData
                });
                showToastNotification('success', 'Category updated successfully');
            } else {
                // Create new category
                const res = await callApi(API_URL_2 + API_ENDPOINT.page + 'categories', {
                    method: 'POST',
                    data: postData
                });
                categoryId = res.id;
                showToastNotification('success', 'Category added successfully');
            }
            if (seoFormRef.current) {
                const isValid = await seoFormRef.current.triggerValidation();
                const seoFormData = seoFormRef.current.getFormData();
                if (isValid) {
                    let newSeoData = {
                        ...seoFormData,
                        entityId: categoryId,
                        entityType: 'category',
                    }
                    submitSEOData(newSeoData, seoData.id);
                } else {
                    showToastNotification('danger', 'SEO form has validation errors');
                    // You can focus on the first error or show a message
                }
            }
            navigate('/categories');
        } catch (error: any) {
            showToastNotification('danger', error.message);
        } finally {
            setLoadingButton(false);
        }
    };

    const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        if (file.size > 1 * 1024 * 1024) {
            return showToastNotification('danger', `"${file.name}" exceeds 1MB limit.`);
        }

        try {
            const uploadedUrl = await uploadImage(file);
            setImageURL(uploadedUrl);
        } catch (err) {
            console.error(err);
        }
    };

    const handleDeleteImage = async (imageURL: string) => {
        await deleteFile(imageURL);
        setImageURL('');
    };

    const handleCancel = () => {
        navigate('/categories');
    };

    if (loading) {
        return (
            <Layout>
                <div className="d-flex justify-content-center align-items-center" style={{ height: '50vh' }}>
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                </div>
            </Layout>
        );
    }

    const headerText = editingCategory
        ? 'Update Category'
        : isSubCategory
            ? 'Add Sub Category'
            : 'Add Category';

    return (
        <Layout>
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h1 className="app-page-title">{headerText}</h1>
                <button
                    className="btn btn-outline-secondary"
                    onClick={handleCancel}
                >
                    <i className="bi bi-arrow-left me-2"></i>
                    Back to Categories
                </button>
            </div>

            <div className="row">
                <div className="col-lg-12 mx-auto">
                    <div className="app-card app-card-settings shadow-sm p-4">
                        <div className="app-card-body">
                            <form onSubmit={handleSubmit(onSubmit)}>
                                {isSubCategory && parentCategory && (
                                    <div className="alert alert-info">
                                        <strong>Parent Category:</strong> {parentCategory.title}
                                    </div>
                                )}

                                <div className="row">
                                    <div className="mb-3 col-12">
                                        <InputField
                                            type="text"
                                            register={register('title', { required: 'Category name is required' })}
                                            name="title"
                                            label="Category Name"
                                            placeholder="Enter category name"
                                        />
                                        <ErrorMessage message={errors.title?.message?.toString()} />
                                    </div>
                                    <div className='mb-3  col-12'>
                                        <label htmlFor="slug" className="form-label">Slug</label>
                                        <input
                                            type="text"
                                            {...register('slug', {
                                                required: 'Slug is required',
                                                pattern: {
                                                    value: /^[a-z0-9]+(?:-[a-z0-9]+)*$/,
                                                    message: 'Slug must be URL-friendly: lowercase letters, numbers, hyphens. No spaces or special characters.'
                                                }
                                            })}
                                            className='form-control'
                                            name="slug"
                                            placeholder="Enter slug"
                                            onFocus={handleSlugFocus}
                                            onChange={() => setIsSlugTouched(true)}
                                        />
                                        <ErrorMessage message={errors.slug?.message?.toString()} />
                                    </div>

                                    <div className="mb-3 col-12">
                                        <Checkbox
                                            register={register('isRecommended', { required: false })}
                                            label="Recommended"
                                        />
                                    </div>

                                    {!isSubCategory && (
                                        <div className='mb-3'>
                                            <label className='mb-2 form-label fw-bold'>Category Filters</label>
                                            <div className='d-flex align-items-center row-gap-1 column-gap-3 gap-3 flex-wrap'>
                                                <div className="">
                                                    <Checkbox
                                                        register={register('cut', { required: false })}
                                                        label="Cut"
                                                    />
                                                </div>
                                                <div className="">
                                                    <Checkbox
                                                        register={register('state', { required: false })}
                                                        label="State"
                                                    />
                                                </div>
                                                <div className="">
                                                    <Checkbox
                                                        register={register('brand', { required: false })}
                                                        label="Brand"
                                                    />
                                                </div>
                                                <div className="">
                                                    <Checkbox
                                                        register={register('grade', { required: false })}
                                                        label="Grade"
                                                    />
                                                </div>
                                                <div className="">
                                                    <Checkbox
                                                        register={register('origin', { required: false })}
                                                        label="Origin"
                                                    />
                                                </div>
                                                <div className="">
                                                    <Checkbox
                                                        register={register('valueAddition', { required: false })}
                                                        label="Savory Essentials"
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    <div className="mb-3 col-12 image-upload">
                                        <div className='form-label fw-bold'>
                                            Category Image
                                        </div>
                                        <div className="row">
                                            <div className="col-12">
                                                <label htmlFor="file-input" className="choosefilenew position-relative">
                                                    <input
                                                        type="file"
                                                        className=''
                                                        id="file-input"
                                                        accept="image/*"
                                                        hidden
                                                        onChange={(e) => handleFileChange(e)}
                                                    />
                                                    <div>
                                                        <div className='mb-2'><i className="bi fs-4 bi-upload"></i></div>
                                                        Upload Image
                                                    </div>
                                                </label>
                                            </div>
                                            <div className="col-12 mt-3">
                                                <div className='addimagefiles w-100'>
                                                    {imageURL && (
                                                        <div className="image-card w-100">
                                                            <img src={imageURL} alt="Category" className="image-preview w-100" />
                                                            <button
                                                                type="button"
                                                                className='btn btn-danger px-2 py-1 text-white position-absolute'
                                                                onClick={() => handleDeleteImage(imageURL)}
                                                            >
                                                                <i className='bi bi-trash3'></i>
                                                            </button>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                        <ErrorMessage message={errors.icon?.message?.toString()} />
                                        <ErrorMessage message={error?.toString()} />
                                        <small className="text-muted">
                                            Maximum file size: 1MB. Supported formats: JPG, PNG, GIF.
                                        </small>
                                    </div>
                                </div>
                                <div className="mb-3 col-12">
                                    <SeoForm
                                        ref={seoFormRef}
                                        entityId={id || ''}
                                        entityType="category"
                                        initialData={seoData}
                                        isRequired={false}
                                    />
                                </div>
                                <div className="d-flex gap-2 justify-content-end mt-4">
                                    <button
                                        type="button"
                                        className="btn btn-outline-secondary px-4"
                                        onClick={handleCancel}
                                    >
                                        Cancel
                                    </button>
                                    <Button
                                        loading={loadingButton}
                                        className="btn btn-primary text-white px-4"
                                        type="submit"
                                        text={editingCategory ? 'Update Category' : 'Save Category'}
                                    />
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </Layout>
    );
};

export default AddCategory;